import { Theme } from '@eversports/react-components/design-tokens/theme'
import Button from '@eversports/react-components/primitives/Button'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import { useLocation } from 'react-router-dom'
import React from 'react'

import { Localized } from '../../../localization/react'
import BookableItem, { AssignedSpot, EventType, TimeRange } from '../../../typings/BookableItem'
import { SettleWith } from '../../../typings/Product'

import Routes from '../../../data/routes'
import { useCalendarExport } from '../../../data/useCalendarExport'
import Address from './Address'
import BookingDetailsCancellationConditions from './BookingDetailsCancellationConditions'
import BookingDetailsName from './BookingDetailsName'
import BookingDetailsSettleWith from './BookingDetailsSettleWith'
import BookingDetailsTimeBlock from './BookingDetailsTimeBlock'
import { BookingDetailsAssignedSpot } from './BookingDetailsAssignedSpot'

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontSize: theme.fontSizing.large,
    [theme.breakpoints.down('md')]: {
      fontSize: theme.fontSizing.default,
    },
  },
}))

interface Props {
  bookableItem: BookableItem
  settleWith?: SettleWith | null
  hasSpotBooking: boolean
  assignedSpot?: AssignedSpot
  eventSessions?: Array<TimeRange>
}

const BookableItemBookingDetailsContentInformation: React.FC<React.PropsWithChildren<Props>> = ({
  hasSpotBooking,
  bookableItem,
  settleWith,
  assignedSpot,
  eventSessions,
}) => {
  const location = useLocation()
  const isConfirmationPage = location.pathname === Routes.CONFIRMATION
  const { exportToCalendar } = useCalendarExport()

  const handleAddToCalendar = (): void => {
    if (bookableItem.__typename === 'EventBookableItem' && eventSessions) {
      exportToCalendar(bookableItem, eventSessions)
    }
  }

  switch (bookableItem.__typename) {
    case 'CourtBookableItem':
      return (
        <div>
          <Flex flexDirection="column" marginBottom="wide">
            <BookingDetailsName
              type="court"
              venueName={bookableItem.venue.name}
              sport={bookableItem.sport.name}
              courtName={bookableItem.court.name}
            />
            <BookingDetailsCancellationConditions cancellationConditions={bookableItem.cancellationCondition} />
          </Flex>
          <Flex flexDirection="column" verticalSpacing="default">
            <Address location={bookableItem.venue.location!} />
            <BookingDetailsTimeBlock type="court" timeRange={bookableItem.timeRange} />
          </Flex>
        </div>
      )
    case 'EventBookableItem':
      return (
        <div>
          <Flex flexDirection="column" marginBottom="wide">
            <BookingDetailsName
              type="event"
              venueName={bookableItem.venue.name}
              sport={bookableItem.sport.name}
              activityName={bookableItem.activityName}
            />
            <BookingDetailsCancellationConditions cancellationConditions={bookableItem.cancellationCondition} />
          </Flex>
          <Flex flexDirection="column" verticalSpacing="default" marginBottom={isConfirmationPage ? 'wide' : 'default'}>
            <Address location={bookableItem.location} />
            {bookableItem.eventType === EventType.PRIVATE && bookableItem.eventSessions[0]?.trainer && (
              <Flex alignItems="center">
                <Icon icon={Icons.PERSON} color="subtleText" marginRight="dense" />
                <Text fontWeight="bold" size="small">
                  {bookableItem.eventSessions[0].trainer.nickname}
                </Text>
              </Flex>
            )}
            {settleWith && settleWith.__typename === 'Ticket' ? (
              <BookingDetailsTimeBlock type="event" timeRanges={settleWith.validForSessions} />
            ) : (
              <BookingDetailsTimeBlock type="event" timeRanges={bookableItem.eventSessions} />
            )}
            {settleWith && <BookingDetailsSettleWith settleWith={settleWith} />}
            {hasSpotBooking && isConfirmationPage && <BookingDetailsAssignedSpot assignedSpot={assignedSpot} />}
          </Flex>
          {eventSessions && eventSessions.length > 0 && isConfirmationPage && (
            <Button size="small" variant="secondary" onClick={handleAddToCalendar} fullWidth={false}>
              <Localized id="confirmation-button-add-to-calendar" />
              <Icon icon={Icons.CALENDAR} color="primary" size="small" marginLeft="dense" />
            </Button>
          )}
        </div>
      )
    default:
      return assertNever(bookableItem)
  }
}

const BookableItemBookingDetailsContent: React.FC<React.PropsWithChildren<Props>> = ({
  bookableItem,
  settleWith,
  hasSpotBooking,
  assignedSpot,
  eventSessions,
}) => {
  const classes = useStyles()

  return (
    <Flex flexDirection="column" verticalSpacing={['dense', 'wide']}>
      <Heading is="h2" className={classes.heading} marginTop={['default', 'default', 'none']}>
        <Localized id="booking-details" />
      </Heading>
      <BookableItemBookingDetailsContentInformation
        bookableItem={bookableItem}
        settleWith={settleWith}
        hasSpotBooking={hasSpotBooking}
        assignedSpot={assignedSpot}
        eventSessions={eventSessions}
      />
    </Flex>
  )
}

export default BookableItemBookingDetailsContent
